import React, { useState, useEffect } from 'react';
import { Button, TextField, Typography, Box, Paper, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useDispatch } from 'react-redux';
import { resetPassword } from '../slices/accountSlice';
import { clearSession } from '../utils/clearSession';

const ResetPasswordPage = () => {
  const accessToken = localStorage.getItem('accessToken');
  const user = localStorage.getItem('user');
  const userObj = JSON.parse(user);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [gradientAngle, setGradientAngle] = useState(0);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setGradientAngle(prev => (prev + 0.5) % 360);
    }, 50);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    document.title = "إعادة تعيين كلمة المرور";
  }, []);

  const validatePassword = (password) => {
    return password.length >= 8 && /[0-9]/.test(password) && /[A-Za-zأ-ي]/.test(password);
  };

    const handleLogout = () => {
    clearSession(); // ✅ Clear all stored data (keeps device preferences)
    navigate('/');        // 🔁 Redirect to home or login
  };

  const handleReset = async (e) => {
  e.preventDefault();
  setLoading(true);
  setError('');
  setSuccess(false);

  if (!newPassword || !confirmPassword) {
    setError('يرجى إدخال كلمة المرور الجديدة وتأكيدها');
    setLoading(false);
    return;
  }
  if (newPassword !== confirmPassword) {
    setError('كلمتا المرور غير متطابقتين');
    setLoading(false);
    return;
  }
  if (!validatePassword(newPassword)) {
    setError('كلمة المرور يجب أن تكون 8 أحرف على الأقل وتحتوي على رقم وحرف');
    setLoading(false);
    return;
  }

  try {
    const resultAction = await dispatch(resetPassword({ newPassword }));

    if (resetPassword.fulfilled.match(resultAction)) {
      setSuccess(true);
      handleLogout(); // Automatically log out after successful reset
    } else {
      setError(
        resultAction.payload?.detail ||
        resultAction.payload?.non_field_errors?.[0] ||
        'حدث خطأ ما'
      );
    }
  } catch {
    setError('فشل الاتصال بالخادم');
  } finally {
    setLoading(false);
  }
};


  return (
    // 🎨 Design System Applied
    <Box sx={{
      direction: 'rtl',
      fontFamily: 'Cairo, Tajawal, sans-serif',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      p: 2,
      background: `linear-gradient(${gradientAngle}deg, #00BFFF 0%, #4338CA 55%, #5B21F4 100%)`,
      transition: 'background 0.3s ease-out',
    }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        style={{ width: '100%', maxWidth: 400 }}
      >
        <Paper elevation={0} sx={{
          width: '100%',
          maxWidth: 400,
          mx: 'auto',
          borderRadius: 3,
          textAlign: 'center',
          backgroundColor: '#FFFFFF',
          boxShadow: '0 2px 12px rgba(67,56,202,0.07)',
          overflow: 'hidden',
        }}>
          {/* Gradient accent bar */}
          <Box sx={{
            height: 6,
            background: 'linear-gradient(90deg, #00BFFF 0%, #4338CA 60%, #5B21F4 100%)',
          }} />

          <Box sx={{ p: { xs: 3, sm: 4 } }}>
            {/* Brand */}
            <Typography
              sx={{
                fontFamily: 'Cairo, Tajawal, sans-serif',
                fontWeight: 800,
                fontSize: '1rem',
                color: '#4338CA',
                mb: 2,
                letterSpacing: '0.5px',
              }}
            >
              دفتر نون
            </Typography>

            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              style={{ marginBottom: '4px' }}
            >
              <Typography
                sx={{
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  fontWeight: 800,
                  fontSize: '1.5rem',
                  color: '#1E1B4B',
                  mb: 0.5,
                }}
              >
                إعادة تعيين كلمة المرور
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Cairo, Tajawal, sans-serif',
                  fontSize: '0.875rem',
                  color: '#6B7280',
                }}
              >
                أدخل كلمة المرور الجديدة لتأمين حسابك
              </Typography>
            </motion.div>

            <Box component="form" sx={{ mt: 3 }} onSubmit={handleReset}>
              <TextField
                fullWidth
                label="كلمة المرور الجديدة"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                margin="dense"
                variant="outlined"
                dir="rtl"
                type="password"
                sx={{
                  mb: 1.5,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    '&.Mui-focused fieldset': { borderColor: '#4338CA' },
                  },
                  '& label': { fontFamily: 'Cairo, Tajawal, sans-serif' },
                  '& label.Mui-focused': { color: '#4338CA' },
                }}
              />
              <TextField
                fullWidth
                label="تأكيد كلمة المرور الجديدة"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                margin="dense"
                variant="outlined"
                dir="rtl"
                type="password"
                sx={{
                  mb: 1.5,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    '&.Mui-focused fieldset': { borderColor: '#4338CA' },
                  },
                  '& label': { fontFamily: 'Cairo, Tajawal, sans-serif' },
                  '& label.Mui-focused': { color: '#4338CA' },
                }}
              />
              <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  sx={{
                    mt: 1.5,
                    py: 1.2,
                    borderRadius: 2.5,
                    fontWeight: 700,
                    fontSize: '1rem',
                    textTransform: 'none',
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                    bgcolor: '#4338CA',
                    boxShadow: 'none',
                    '&:hover': { bgcolor: '#3730A3', boxShadow: 'none' },
                  }}
                  type="submit"
                  disabled={loading}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'تعيين كلمة المرور الجديدة'}
                </Button>
              </motion.div>
              {error && (
                <Box sx={{
                  mt: 2,
                  py: 1.25,
                  px: 2,
                  borderRadius: 2,
                  bgcolor: '#FEE2E2',
                  textAlign: 'center',
                }}>
                  <Typography sx={{
                    color: '#DC2626',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                  }}>
                    {error}
                  </Typography>
                </Box>
              )}
              {success && (
                <Box sx={{
                  mt: 2,
                  py: 1.25,
                  px: 2,
                  borderRadius: 2,
                  bgcolor: '#DCFCE7',
                  textAlign: 'center',
                }}>
                  <Typography sx={{
                    color: '#16A34A',
                    fontWeight: 700,
                    fontSize: '0.875rem',
                    fontFamily: 'Cairo, Tajawal, sans-serif',
                  }}>
                    تم تعيين كلمة المرور الجديدة بنجاح
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Paper>
      </motion.div>
    </Box>
  );
};

export default ResetPasswordPage;
